import { Component, EventEmitter, Input, OnInit, Output, ViewChild, OnDestroy } from '@angular/core';
import { GridResizeEvent, GridSettings } from 'src/app/core/model/grid-settings';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { ReportResultListService } from 'src/app/core/service/grid-data/grid-data-report-result.service';
import { ReportService } from 'src/app/core/service/report.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import
{
  ColumnReorderEvent, ColumnVisibilityChangeEvent, DataStateChangeEvent, GridComponent, GridDataResult
} from '@progress/kendo-angular-grid';
import { GridQueryUtils } from 'src/app/core/utility/grid-query-utils';
import { IntlService } from '@progress/kendo-angular-intl';
import { cloneDeep } from 'lodash';
import { KendoMessageService } from 'src/app/core/service/kendo-message.service';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { SelectEvent } from '@progress/kendo-angular-layout';
import { TranslateService } from '@ngx-translate/core';
import { UntypedFormArray } from '@angular/forms';
import { FilterFunction } from '../report-list/report-edit';
import { FilterExpression } from '@progress/kendo-angular-filter';
import { Subscription } from 'rxjs';
import { MessengerService } from 'src/app/core/service/messenger.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { Code } from 'src/app/core/model/dictionary';
import { saveAs } from '@progress/kendo-file-saver';

@Component({
  standalone: false,
  selector: 'app-report-grid',
  templateUrl: './report-grid.component.html',
  styleUrls: ['./report-grid.component.scss']
})
export class ReportGridComponent implements OnInit, OnDestroy
{
  @ViewChild(GridComponent) public grid: GridComponent;

  @Input() public designMode = false;
  @Input() public parentWindowState = undefined;
  @Input() public sqlViewName = undefined;
  @Input() public gridSettings = undefined;
  @Input() public editForm: any;
  @Input() public reportId = undefined;
  @Input() public reportName = undefined;

  @Output() public addFunctionEvent: EventEmitter<any> = new EventEmitter();
  @Output() public removeFunctionEvent: EventEmitter<any> = new EventEmitter();
  @Output() public gridSettingsChanged: EventEmitter<GridSettings> = new EventEmitter();

  public extractLimit: number;
  public view: GridViewManager;
  public total = 0;
  public enableAdvanced = false;
  public gridView: GridDataResult;
  public gridData: any = {};
  public exporting = false;
  public pageSizes = [5, 10, 25, 50, 100];
  public filtersText = '';
  public state: any;
  public currentFilters = { filters: [] };
  public fields: FilterExpression[] = [];
  public extractErrorTitleText = '';
  public extractLimitText = '';
  public filterFunctions: UntypedFormArray;
  public usedFilterFunctions = false;
  public columnDataTypes: Array<{ text: string; value: string }>;
  public extractFunctions: Code[];
  public firstOpen = true;
  public loaded = false;

  private functionChanged = false;
  private messagingSubscription: Subscription;
  private filterChanged = false;

  constructor(
    private appConfigService: AppConfigService,
    private messageHandlerService: MessageHandlerService,
    private reportResultListService: ReportResultListService,
    private settingService: SettingService,
    private intl: IntlService,
    private kendoMessageService: KendoMessageService,
    private translate: TranslateService,
    private dictionaryService: DictionaryService,
    private messengerService: MessengerService,
    private reportService: ReportService)
  {
    this.view = new GridViewManager(this.appConfigService.getConfig().pageSize);
  }

  /**
   * Provide loading property from Service.
   */
  public get loading(): boolean
  {
    return this.reportResultListService.loading;
  }

  public get showExtractLimitHint(): boolean
  {
    return this.extractLimit > 0 && this.total > this.extractLimit;
  }

  public ngOnInit(): void
  {
    this.loaded = false;
    this.firstOpen = true;
    this.columnDataTypes = this.reportService.columnDataTypes;

    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.extractLimit = sessionSetting.extractLimit;
        this.translate.get(['EXTRACT-LIMIT-HINT-WARNING']).subscribe({
          next: (res) =>
          {
            this.extractLimitText = res['EXTRACT-LIMIT-HINT-WARNING'].replace('{{limit}}', this.extractLimit);
          }
        });
      }
    });

    this.translate.get(['EXTRACT-ERROR']).subscribe({
      next: (res) =>
      {
        this.extractErrorTitleText = res['EXTRACT-ERROR'];
      }
    });

    this.dictionaryService.getCachedExtractFunctions().subscribe({
      next: (data) =>
      {
        this.extractFunctions = data;
      }
    });

    // In case of apply, we want to call initGridColumns
    if (this.messagingSubscription === undefined)
    {
      this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
        next: (message) =>
        {
          if (message.type === 'afterExtractSaveAndApply') { this.initGridColumns(this.filterChanged); }
          else
            if (message.type === 'refreshReportFilterText')
            {
              // If applied, we assumed filter have changed and therefore we will run the query again on firsttab selection
              if (message.data && message.data.apply) 
              {
                this.filterChanged = true;
              }
              this.refreshFilterText();
            }
        }
      });
    }
    this.state = { skip: 0, filter: { logic: 'and', filters: [] }, take: this.appConfigService.getConfig().pageSize };
    this.initGridColumns();
    this.refreshFilterText();
  }

  public ngOnDestroy(): void
  {
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
  }

  /**
   * Reorder grid column order event
   *
   * @param e ColumnReorderEvent event
   */
  public onReorder(e: ColumnReorderEvent): void
  {
    const reorderedColumn = this.view.columnsConfig.splice(e.oldIndex, 1);
    this.view.columnsConfig.splice(e.newIndex, 0, ...reorderedColumn);
    this.gridChanges();
  }

  /**
   * Grid onResize event.
   *
   * @param e Grid on resize column event.
   */
  public onResize(event: Array<GridResizeEvent>): void
  {
    event.forEach(item =>
    {
      const field = this.view.columnsConfig.find((col) => col.field === item.column.field);
      if (field !== undefined)
      {
        field.width = item.newWidth;
      }
    });
    this.gridChanges();
  }

  /**
   * Grid column visibility changed.
   *
   * @param event Grid column visibility event.
   */
  public onVisibilityChange(event: ColumnVisibilityChangeEvent): void
  {
    event.columns.forEach((column: any) =>
    {
      const field = this.view.columnsConfig.find((col) => col.field === column.field);
      if (field !== undefined)
      {
        field.hidden = column.hidden;
      }
    });
    this.gridChanges();
  }

  /**
   * Data change event handler.
   *
   * @param stateEvent State of grid operations
   */
  public dataStateChange(stateEvent: DataStateChangeEvent, runReport: boolean): void
  {
    this.state = stateEvent;
    if (this.gridSettings.state === undefined)
    {
      this.gridSettings = {};
    }
    this.gridSettings.state = stateEvent;
    this.gridChanges();

    if (runReport)
    {
      this.runReport();
    }
  }

  /**
   * Export to Excel and running report for exporting all records.
   */
  public exportToExcel(): void
  {
    this.runReport(true);
  }

  /**
   * Toggle advanced mode.
   *
   * @param enabled Boolean to enable or disable the advance mode.
   */
  public toggleAdvance(enabled: boolean): void
  {
    this.enableAdvanced = enabled;
    setTimeout(() => { this.autoFitColumns(); });
  }

  /**
   * Event occurs when the filter component is changed.
   *
   * @param newFilter New filters
   */
  public onFilterChange(newFilter: CompositeFilterDescriptor): void
  {
    this.filterChanged = true;
    this.state.filter = newFilter;
    this.dataStateChange(this.state, false);      // Emit filter changes but do not run the report until the grid tab is selected
    this.refreshFilterText();
  }

  /**
   * Event occurs when the design tab is selected.
   *
   * @param e Tab event
   */
  public onTabSelect(e: SelectEvent): void
  {
    if (e.index === 0 && (this.filterChanged || this.functionChanged || (this.designMode && this.firstOpen)))
    {
      this.state.skip = 0;                        // Reset page
      this.currentFilters = this.state.filter;    // Update the current filters from the state.filter object
      this.dataStateChange(this.state, true);     // Re-run the report when grid tab is selected and the filter have been changed
      this.filterChanged = false;
      this.functionChanged = false;
    }
    else if (e.index === 1)
    {
      this.updateCurrentFilters();
    }
    else if (e.index === 2)
    {
      this.functionChanged = true;  // Not necessary changed but want to force a dataStateChange
    }
    this.firstOpen = false;
  }

  /**
   * Reset grid columns with default state
   */
  public resetGridColumns(): void
  {
    this.gridSettings.columnsConfig = null;
    this.gridSettings.state.skip = 0;
    this.initGridColumns();
    this.gridChanges();
  }

  public addFunction(_id: number = null, _version = 0, _name: string = null, _functionCode: string = null, _dataType: string = null,
    _columnNameVar1: string = null, _columnNameVar2: string = null, _columnNameVar3: string = null): void
  {
    const obj: FilterFunction = {
      id: _id,
      version: _version,
      name: _name,
      functionCode: _functionCode,
      dataType: _dataType,
      columnNameVar1: _columnNameVar1,
      columnNameVar2: _columnNameVar2,
      columnNameVar3: _columnNameVar3
    };
    this.addFunctionEvent.emit(obj);
  }

  public removeFunction(index: number, _id: number): void
  {
    this.usedFilterFunctions = false;

    // If the function is saved, check if in used in filters prior deleting
    if (_id)
    {
      this.updateCurrentFilters();
      this.checkFunctionInUser(this.currentFilters.filters,
        this.editForm.controls.filterFunctions.controls[index].controls.name.value);
    }

    if (this.usedFilterFunctions)
    {
      this.messageHandlerService.displayError('FUNCTION-IN-USE');
    }
    else
    {
      this.removeFunctionColumnByName(this.editForm.controls.filterFunctions.controls[index].controls.name.value);
      this.removeFunctionEvent.emit({ i: index, id: _id });
    }
  }

  public copyFunctionCode(e: Code, index: number): void
  {
    this.editForm.controls.filterFunctions.controls[index].controls.name.setValue(e.code);
    this.editForm.controls.filterFunctions.controls[index].controls.functionCode.setValue(e.alternateCode);
    this.editForm.controls.filterFunctions.controls[index].controls.dataType.setValue('STRING');
  }

  public formatFlags(value: string): string
  {
    if (value === null || value === undefined || value === '')
    {
      return '';
    }

    return value.split(',').map((flag) =>
    {
      const parts = flag.split('|');
      if (parts.length >= 3)
      {
        parts[2] = this.formatFlagDate(parts[2]);
        return parts.join('|');
      }
      return flag;
    }).join(',');
  }

  public columnSelector(selectAll: boolean): void
  {
    if (this.view.columnsConfig.length > 0)
    {
      let count = 0;
      this.view.columnsConfig.forEach((x: any) =>
      {
        x.hidden = !selectAll && count > 0;
        count += 1;
      });
      this.autoFitColumns();
      this.gridChanges();
    }
  }

  private formatFlagDate(dateValue: string): string
  {
    if (!dateValue)
    {
      return dateValue;
    }

    const date = dateValue.trim().length === 10
      ? new Date(`${dateValue.trim()}T12:00:00.000Z`)
      : new Date(dateValue.trim().replace('+0000', 'Z'));

    return this.intl.formatDate(date, 'd');
  }

  private removeFunctionColumnByName(columnName: string): void
  {
    const columnsConfig = this.view.columnsConfig;
    this.view.columnsConfig = columnsConfig.filter(x => x.field !== columnName);
    if (this.state.sort)
    {
      this.state.sort = this.state.sort.filter(x => x.field !== columnName);
    }
    this.gridChanges();
  }

  private checkFunctionInUser(filters: any, searchField: string): void
  {
    filters.forEach((data: any) =>
    {
      if (data.filters !== undefined)
      {
        this.checkFunctionInUser(data.filters, searchField);
      }
      else
      {
        if (data.field === searchField)
        {
          this.usedFilterFunctions = true;
        }
      }
    });
  }

  private updateCurrentFilters(): void
  {
    if (this.gridSettings.state !== undefined && this.gridSettings.state !== null)
    {
      this.currentFilters = this.gridSettings.state.filter; // Update filters in case filter are set from grid columns
    }
    else
    {
      this.currentFilters = this.state.filter;
    }
  }

  private refreshFilterText(): void
  {
    if (this.gridSettings.state !== undefined && this.gridSettings.state !== null)
    {
      const stateFilters = cloneDeep(this.gridSettings.state.filter.filters);
      let text = '';
      text = GridQueryUtils.filtersToText(stateFilters, this.view.columnsConfig, 'and', text, this.intl, this.kendoMessageService);
      this.filtersText = text;
    }
    else
    {
      this.filtersText = '';
    }
  }

  /**
   * Grid columns initialization
   */
  private initGridColumns(filterChanged: boolean = false): void
  {
    if (this.sqlViewName !== undefined)
    {
      this.filterChanged = filterChanged;
      this.view.columnsConfig = [];
      this.reportService.getReportMeta(this.sqlViewName, this.reportId).subscribe({
        next: (data) =>
        {
          const columns = [];
          data.forEach((column: any) =>
          {
            if (column.data_type === 'boolean')
            {
              columns.push({ field: column.column_name, filter: 'boolean', hidden: false, dataType: column.data_type });
            }
            else if (column.data_type.includes('timestamp'))
            {
              columns.push({ field: column.column_name, filter: 'date', format: '{0:g}', hidden: false, dataType: column.data_type });
            }
            else if (column.data_type.includes('date'))
            {
              columns.push({ field: column.column_name, filter: 'date', format: '{0:d}', hidden: false, dataType: column.data_type });
            }
            else if (column.data_type === 'bigint' || column.data_type.includes('integer'))
            {
              columns.push({ field: column.column_name, filter: 'numeric', format: '#', decimals: 0, hidden: false, dataType: column.data_type });
            }
            else if (column.data_type.includes('double'))
            {
              columns.push({ field: column.column_name, filter: 'numeric', format: 'n2', hidden: false, dataType: column.data_type });
            }
            // patient_flags and case_flags contain “flag name|category_name|created_on”; this will ensure created_on is properly formatted
            else if (column.column_name === 'patient_flags' || column.column_name === 'case_flags')
            {
              columns.push({ field: column.column_name, hidden: false, dataType: column.data_type, valueFormat: 'flags' });
            }
            else
            {
              columns.push({ field: column.column_name, hidden: false, dataType: column.data_type });
            }
          });

          // Set grid columns
          this.view.columnsConfig = columns;

          // Convert grid columns definition to FilterExpression. If filter is undefined, this is a string editor.
          // Convert numeric to number.
          this.fields = columns.map(c => (
            {
              field: c.field,
              editor: c.filter !== undefined ? (c.filter === 'numeric' ? 'number' : c.filter) : 'string',
              editorFormat: c.filter === 'numeric' ? c.format : null
            }));

          // Apply filter from report
          if (this.gridSettings !== undefined && this.gridSettings !== null)
          {
            const gridSettings: GridSettings = this.gridSettings;
            if (gridSettings.columnsConfig !== undefined && gridSettings.columnsConfig !== null)
            {
              const loadedColumns = GridQueryUtils.setBaseColumnsSetting(this.translate,
                JSON.stringify(gridSettings.columnsConfig), this.view.columnsConfig);
              this.view.columnsConfig = loadedColumns;
            }
            if (gridSettings.state !== undefined && gridSettings.state !== null)
            {
              if (gridSettings.state && gridSettings.state.filter && gridSettings.state.filter.filters)
              {
                GridQueryUtils.convertFiltersToJSDates(gridSettings.state.filter.filters, this.view.columnsConfig);
              }
              this.state = gridSettings.state;
              this.currentFilters = gridSettings.state.filter;
            }
          }
          if (!this.designMode)
          {
            this.runReport();
          }
          this.loaded = true;
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
  }

  /**
   * Grid settings have been changed. Emit new grid config changes.
   */
  private gridChanges(): void
  {
    if (this.gridSettingsChanged !== undefined)
    {
      const gridConfig: GridSettings = { columnsConfig: this.view.columnsConfig, state: this.state };
      this.gridSettingsChanged.emit(gridConfig);
    }
  }

  /**
   * Get the report data by fetching the view.
   *
   * @param runExport Set to true when want to export to Excel.
   */
  private runReport(runExport = false): void
  {
    const savedSkip = this.state.skip;
    const savedTake = this.state.take;

    // If export to Excel, we need to fetch all the rows.
    if (runExport)
    {
      this.exporting = true;
      this.state.skip = 0;
      this.state.take = this.extractLimit;
    }

    const reportIdString = this.reportId ? `&reportId=${this.reportId}` : '';
    const exportFormat = runExport ? `&format=xlsx` : '';
    const additionalQueryString = `?view=${this.sqlViewName.toLowerCase()}${reportIdString}${exportFormat}`;
    const httpPostBody = GridQueryUtils.normalizeFilters(this.state);

    if (runExport)
    {
      httpPostBody.exportColumns = this.view.columnsConfig.filter(x => !x.hidden).map((c: any) => ({ fieldName: c.field }));
    }

    this.reportResultListService.query((result: any) =>
    {
      if (!runExport)
      {
        result.data.forEach(obj =>
        {
          // Convert dates to JS dates
          Object.keys(obj).forEach(e =>
          {
            const columnDef = this.view.columnsConfig.find((col) => col.field === e);
            if (columnDef !== undefined && columnDef.filter === 'date' && obj[e] !== null)
            {
              obj[e] = columnDef.dataType === 'date' ? new Date(`${obj[e]}T12:00:00.000Z`) : new Date(obj[e].replace('+0000', 'Z'));
            }
          });
        });

        this.gridView = {
          data: (result) ? this.gridData = result.data : this.gridData = [],
          total: (result) ? this.total = result.total : this.total = 0
        };

        this.exporting = false;
        setTimeout(() =>
        {
          this.autoFitColumns();
          this.refreshFilterText();
        });
      }
      else
      {
        this.state.skip = savedSkip;
        this.state.take = savedTake;
        if (result.total === 0 || !result.export)
        {
          this.messageHandlerService.displayWarning('NO-DATA-TO-EXPORT');
        }
        else
        {
          saveAs(result.export, this.getFileExportName(this.sqlViewName));
          if (this.extractLimit > 0 && result.total > this.extractLimit)
          {
            this.displayExtractLimitExportWarning(result.total);
          }
          else
          {
            this.messageHandlerService.displaySuccess('DATA-EXPORTED-SUCCESSFULLY');
          }
        }
        this.exporting = false;
        setTimeout(() => { this.autoFitColumns(); });
      }
    }, additionalQueryString, (errorResponse: any) =>
    {
      this.messageHandlerService.displayDirectMessage('error', errorResponse.error ? errorResponse.error.message : '',
        this.extractErrorTitleText);

      this.state.skip = savedSkip;
      this.state.take = savedTake;

    }, null, 'post', httpPostBody);
  }

  private autoFitColumns(): void
  {
    if (this.grid !== undefined)
    {
      setTimeout(() =>
      {
        this.grid.autoFitColumns();
        this.grid.scrollTo({ row: 0, column: 0 });
      }, 250);
    }
  }

  /**
   * Return the sql view display name if found otherwise it will return the raw view name.
   *
   * @param sqlViewName SQL View name
   * @returns String
   */
  private getFileExportName(sqlViewName: string): string
  {
    return this.reportName ? this.reportName : sqlViewName;
  }

  private displayExtractLimitExportWarning(total: number): void
  {
    this.translate.get(['EXTRACT-LIMIT-EXPORT-WARNING']).subscribe({
      next: (res) =>
      {
        const message = res['EXTRACT-LIMIT-EXPORT-WARNING']
          .replace('{{total}}', total)
          .replace('{{limit}}', this.extractLimit);
        this.messageHandlerService.displayDirectMessage('warning', message);
      }
    });
  }
}
